import React, { useState, useEffect, Dispatch, SetStateAction } from 'react';
import { useQuery } from '@tanstack/react-query';
import styles from './CitySearch.module.scss';
import { searchPlaces } from '@src/api/Referencial';
import Input from '../Input';
import ChrevronRight from '@src/assets/ChevronRight.png';

type CitySearchProps = {
    title: string;
    initialValue: string;
    _onSelectExistingCity: ({ city, zipcode }: { city: string; zipcode: string }) => void;
    setCityNotFound: Dispatch<SetStateAction<boolean>>;
    clearInput: () => void;
};

const CitySearch: React.FC<CitySearchProps> = ({ title, setCityNotFound, _onSelectExistingCity, clearInput, initialValue }) => {
    const [query, setQuery] = useState<string>(initialValue);
    const [debouncedQuery, setDebouncedQuery] = useState<string>('');
    const [isSuggestionsOpen, setIsSuggestionsOpen] = useState<boolean>(false);
    const [selected, setSelected] = useState<boolean>(initialValue ? true : false);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedQuery(query), 300);
        return () => clearTimeout(timer);
    }, [query]);

    const { data: suggestions, refetch } = useQuery({
        queryKey: [`searchPlaces`, debouncedQuery],
        queryFn: () => searchPlaces({ country: 'FR', debouncedQuery }),
        enabled: false,
    });

    useEffect(() => {
        if (debouncedQuery && !selected) {
            refetch();
            setIsSuggestionsOpen(true);
        } else {
            setIsSuggestionsOpen(false);
        }
    }, [debouncedQuery, refetch, selected]);

    const _onSelectPlace = (suggestion: { zip_city: string; zip_code: string }) => {
        _onSelectExistingCity({ city: suggestion.zip_city, zipcode: suggestion.zip_code });
        setQuery(`${suggestion.zip_code} ${suggestion.zip_city}`);
        setIsSuggestionsOpen(false);
        setDebouncedQuery('');
        setSelected(true);
    };

    const _onClear = () => {
        clearInput();
        setQuery('');
        setIsSuggestionsOpen(false);
        setDebouncedQuery('');
        setSelected(false);
    };

    const _notFound = () => {
        setCityNotFound(true);
        clearInput();
        setQuery('');
        setIsSuggestionsOpen(false);
        setDebouncedQuery('');
        setSelected(false);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setQuery(e.target.value);
        setSelected(false);
    };

    return (
        <div className={styles.container}>
            <Input
                title={title}
                type="text"
                width="100%"
                isSearch
                value={query.trim() === '' ? '' : query}
                onChange={handleInputChange}
                placeholder="Ex : 75001 Paris"
                clearInput={_onClear}
            />
            {isSuggestionsOpen && suggestions?.length === 0 && (
                <ul className={styles.suggestions}>
                    <li>
                        <button className={styles.notFoundButton} onClick={_notFound}>
                            <p className={styles.notFoundText}>Mon lieu de naissance n’est pas ici</p>
                            <img src={ChrevronRight} alt="ChrevronRight" width={7} height={12} />
                        </button>
                    </li>
                    <li className={styles.errorMessage}>
                        Nous ne parvenons pas à afficher votre adresse, mais pas d'inquiétude, vous pouvez la saisir
                        manuellement
                    </li>
                </ul>
            )}
            {isSuggestionsOpen && suggestions && suggestions?.length > 0 && (
                <ul className={styles.suggestions}>
                    <li>
                        <button className={styles.notFoundButton} onClick={_notFound}>
                            <p className={styles.notFoundText}>Mon lieu de naissance n’est pas ici</p>
                            <img src={ChrevronRight} alt="ChrevronRight" width={7} height={12} />
                        </button>
                    </li>
                    {suggestions.map((suggestion: any, index) => (
                        <li
                            key={index}
                            onClick={() => _onSelectPlace(suggestion)}
                            className={styles.suggestionItem}
                        >
                            {suggestion.zip_code} - {suggestion.zip_city}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default CitySearch;
